/**
 * Formats a chart value according to formatting rules:
 * - Whole numbers displayed without decimal points
 * - Decimal numbers displayed with up to 2 decimal places
 * - Arrays formatted with each element following the same rules
 *
 * @param {number | number[] | null | undefined} value - The value to format
 * @returns {string} The formatted value
 */
const formatChartValue = (value: number | number[] | null | undefined): string => {
  if (value === null || value === undefined) {
    return '-';
  }

  // If value is an array (for range targets)
  if (Array.isArray(value)) {
    const formattedValues = value.map((v) => formatSingleValue(v));
    return `[${formattedValues.join(', ')}]`;
  }

  // Single value
  return formatSingleValue(value);
};

/**
 * Formats a single number value:
 * - For integers: no decimal places
 * - For decimals: up to 2 decimal places with trailing zeros removed
 *
 * @param {number} value - The value to format
 * @returns {string} The formatted value
 */
const formatSingleValue = (value: number): string => {
  // If it's a whole number, don't show decimal places
  if (Number.isInteger(value)) {
    return value.toString();
  }

  // Format to 2 decimal places and remove trailing zeros
  // This handles cases like 3.10 -> 3.1 and 3.00 -> 3
  return value.toFixed(2).replace(/\.?0+$/, '');
};

export { formatChartValue };
